import { tryParse, wait } from "./genericUtils";
import { API_URL } from "../enviormentVariables";

/**
 * sets request authorization headers for an outgoing XMLHttpRequest
 * @param {XMLHttpRequest} request
 * @param {User | undefined} user
 * @returns {void}
 */
export function setAuth(request, user = undefined) {
  //set token for password reset
  if (sessionStorage.getItem("password-reset-token")) {
    let reset_token = sessionStorage.getItem("password-reset-token");
    request.setRequestHeader("Authorization", reset_token);
  }

  if (!localStorage.getItem("jwt") && !sessionStorage.getItem("jwt")) return;
  let jwt = localStorage.getItem("jwt") ?? sessionStorage.getItem("jwt");
  request.setRequestHeader("Authorization", jwt);
  if (user) request.setRequestHeader("id", user.id);
}

/**
 * Attempts to Parse a JWT token to get the payload
 * @param {*} token
 * @returns
 */
export function ParseJWT(token) {
  var base64Url = token.split(".")[1];
  if (!base64Url) return null;
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return tryParse(jsonPayload);
}

/**
 * Uses the default http config to make a GET request to the backend services
 * auto handles user auth config, and response parsing
 * @param {*} path the api url
 * @param {*} user the logged in user object
 * @param {*} headers an array of headers to set for the request [{key, value}]
 * @returns
 */
export function GET(path, user = null, headers = []) {
  return new Promise((resolve) => {
    let request = new XMLHttpRequest();
    request.open("GET", API_URL + path);
    if (user) setAuth(request, user);
    //set headers
    headers.forEach((header) => {
      request.setRequestHeader(header.key, header.value);
    });
    request.send();
    request.onload = () => {
      let response = tryParse(request.response);
      return resolve(response);
    };
    request.onerror = () => {
      return resolve(request);
    };
  });
}

/**
 * Uses the default http config to make a POST request to the backend services
 * auto handles user auth config, and response parsing
 * @param {*} path the api url
 * @param {*} body JSON request body to send in the post
 * @param {*} user the logged in user object
 * @param {*} headers an array of headers to set for the request
 * @returns
 */
export function POST(path, body, user = null, headers = []) {
  return new Promise((resolve) => {
    let request = new XMLHttpRequest();
    request.open("POST", API_URL + path);
    if (user) setAuth(request, user);
    //set headers
    headers.forEach((header) => {
      request.setRequestHeader(header.key, header.value);
    });
    request.send(JSON.stringify(body));
    request.onload = () => {
      let response = tryParse(request.response);
      return resolve(response);
    };
    request.onerror = () => {
      return resolve(request);
    };
  });
}

/**
 * Uses the default http config to make a POST request to the backend services
 * auto handles user auth config, and response parsing
 * @param {*} path the api url
 * @param {*} body JSON request body to send in the put
 * @param {*} user the logged in user object
 * @param {*} headers an array of headers to set for the request
 * @returns
 */
export function PUT(path, body, user = null, headers = []) {
  return new Promise((resolve) => {
    let request = new XMLHttpRequest();
    request.open("PUT", API_URL + path);
    if (user) setAuth(request, user);
    //set headers
    headers.forEach((header) => {
      request.setRequestHeader(header.key, header.value);
    });
    request.send(JSON.stringify(body));
    request.onload = () => {
      let response = tryParse(request.response);
      return resolve(response);
    };
    request.onerror = () => {
      return resolve(request);
    };
  });
}

/**
 * Uses the default http config to make a POST request to the backend services
 * auto handles user auth config, and response parsing
 * @param {*} path the api url
 * @param {*} body JSON request body to send in the patch
 * @param {*} user the logged in user object
 * @param {*} headers an array of headers to set for the request
 * @returns
 */
export function PATCH(path, body, user = null, headers = []) {
  return new Promise((resolve) => {
    let request = new XMLHttpRequest();
    request.open("PATCH", API_URL + path);
    if (user) setAuth(request, user);
    //set headers
    headers.forEach((header) => {
      request.setRequestHeader(header.key, header.value);
    });
    request.send(JSON.stringify(body));
    request.onload = () => {
      let response = tryParse(request.response);
      return resolve(response);
    };
    request.onerror = () => {
      return resolve(request);
    };
  });
}

/**
 * Uses the default http config to make a POST request to the backend services
 * auto handles user auth config, and response parsing
 * @param {*} path the api url
 * @param {*} body JSON request body to send in the delete
 * @param {*} user the logged in user object
 * @param {*} headers an array of headers to set for the request
 * @returns
 */
export function DELETE(path, body, user = null, headers = []) {
  return new Promise((resolve) => {
    let request = new XMLHttpRequest();
    request.open("DELETE", API_URL + path);
    if (user) setAuth(request, user);
    //set headers
    headers.forEach((header) => {
      request.setRequestHeader(header.key, header.value);
    });
    request.send(JSON.stringify(body));
    request.onload = () => {
      let response = tryParse(request.response);
      return resolve(response);
    };
    request.onerror = () => {
      return resolve(request);
    };
  });
}

/**
 * Updates a user's setting
 * @param {*} setting
 * @param {*} value
 * @returns
 */
export async function UpdateSetting(setting, value, user) {
  let response = await PATCH("/user-setting", { setting, value }, user);
  return response;
}

/**
 * DEPRECATED
 * @param url the api url, file bytes should be attempted to stream to
 * @param file the blob object to send to the server
 * @param onProgress if present this function will callback on progress event
 * @param onError  if present this function will callback if there is an error
 * @param chunksize the number of bytes per chunk to send to the server
 * @returns {Promise<null>}
 */
export function chunkedFileUpload(url, file, onProgress, onError, chunksize = 1000) {
  return new Promise((resolve, reject) => {
    let fileReader = new FileReader();
    //read file to determine file size and chunking
    fileReader.onload = async (ev) => {
      //get array buffer after fileRead to determine file size
      let arrayBuffer = ev.target?.result;
      //determine number of chunks needed to uplaod whole file
      let chunkCount = arrayBuffer.byteLength / chunksize;
      let fileName = Math.floor(Math.random() * 1000) + "_" + file.name;
      //create chunks and send them via XMLHttpRequest
      for (let i = 0; i < chunkCount + 1; i++) {
        let chunk = arrayBuffer.slice(i * chunksize, i * chunksize + chunksize);
        await new Promise((resolve, reject) => {
          let request = new XMLHttpRequest();
          request.open("POST", url);
          request.setRequestHeader("Content-Type", "application/octet-stream");
          request.setRequestHeader("file-name", fileName);
          request.send(chunk);
          request.onload = () => {
            let response = request.response;
            if (request.status != 200) {
              return reject(response.message);
            } else {
              return resolve(null);
            }
          };
        }).catch((e) => {
          onError(e);
          return reject(e);
        });
        onProgress(Math.round((i * 100) / chunkCount));
      }
      return resolve(null);
    };
    //initiate file read
    fileReader.readAsArrayBuffer(file);
  });
}

/**
 *
 * @param url the api url, file bytes should be attempted to stream to
 * @param file the blob object to send to the server
 * @param onProgress if present this function will callback on progress event
 * @param onError  if present this function will callback if there is an error
 * @param chunksize the number of bytes per chunk to send to the server
 * @returns {Promise<null>}
 */
export function ChunkedWebsocketFileUpload(
  websocket_url,
  arrayBuffer,
  onStart,
  onProgress,
  onFinishClientUpload,
  onClose,
  onError,
  chunksize = 1000
) {
  return new Promise((resolve, reject) => {
    try {
      onStart();
      //determine number of chunks needed to uplaod whole file
      let fileSize = arrayBuffer.byteLength;
      let chunkCount = fileSize / chunksize;
      //create chunks and send them via WebSocket connection
      const websocket = new WebSocket(websocket_url);
      //assign on start listener
      websocket.onopen = async (event) => {
        for (let i = 0; i < chunkCount + 1; i++) {
          let chunk = arrayBuffer.slice(i * chunksize, i * chunksize + chunksize);
          websocket.send(chunk);
          //wait 30ms to not lock up the browser
          await wait(0.03);
        }
        websocket.send("upload-complete");
        onFinishClientUpload();
        return resolve(null);
      };
      //assign on message listener
      websocket.onmessage = (event) => {
        let response = tryParse(event.data);
        onProgress({ ...response, totalBytes: fileSize });
      };
      //assign on end
      websocket.onclose = (event) => {
        onClose(event);
      };

      websocket.onerror = (e) => {
        onError(e);
      };
    } catch (error) {
      if (onError) {
        onError(error);
      }
    }
  });
}

/**
 * Makes an api call to retrieve incomplete uplaod requests
 * for the specified user and current working directory
 * @param {object} user
 * @param {string} directory
 * @returns
 */
export function GetPendingUploads(user, directory) {
  return new Promise((resolve) => {
    let request = new XMLHttpRequest();
    request.open("GET", API_URL + "/get-pending-uploads");
    setAuth(request, user);
    request.setRequestHeader("x-file-dir", directory);
    request.send();
    request.onload = () => {
      let response = tryParse(request.response);
      if (response.success) {
        return resolve(response);
      } else {
        return resolve(response.error);
      }
    };
    request.onerror = () => {
      return resolve(request);
    };
  });
}

/**
 * attempts to get a file by objectId
 * @param {*} objectId
 * @returns
 */
export function GetFile(objectId, user) {
  return new Promise((resolve) => {
    let request = new XMLHttpRequest();
    request.open("GET", API_URL + "/get-file/" + objectId);
    setAuth(request, user);
    request.responseType = "blob";
    request.send();
    request.onload = () => {
      if (request.status === 200) {
        // Get the response blob
        let blob = request.response;
        // Extract filename from response headers
        let fileName = request.getResponseHeader("x-fileName") || "download";
        let fileExtension = fileName.split(".").pop()?.toLowerCase();

        // Define MIME types
        const mimeTypes = {
          pdf: "application/pdf",
          doc: "application/msword",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          png: "image/png",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          gif: "image/gif",
          bmp: "image/bmp",
          webp: "image/webp",
          svg: "image/svg+xml",
        };

        // Get MIME type based on file extension, or use fallback
        const mimeType = mimeTypes[fileExtension] || "application/octet-stream";

        // Recreate blob with correct MIME type
        blob = new Blob([blob], { type: mimeType });

        // Create blob URL
        let url = URL.createObjectURL(blob);
        return resolve({ fileName, url });
      } else {
        return resolve(null);
      }
    };
  });
}

export function FormatFileName(originalName) {
  let fName = originalName.replaceAll("'", "").replaceAll('"', "");
  return fName;
}
